import { BatchUpdateRequestModel, BatchUpdateSubmissionModel } from 'Data/ViewModels/BatchUpdateRequestModel';
import { hasEmptyValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { map } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { callApi, camelizeObjectKeys } from '../../Helpers/rxjsHelpers';
import endpointConfiguration from './endpointConfiguration';

/**
 * Retrives the endpoint configuration for the worksheet type.
 *
 * Throws an error if the configuration cannot be found or is invalid.
 */
const retrieveEndpointConfig = (worksheetType: string) => {
  let config = endpointConfiguration[worksheetType];
  if (!config) {
    throw new Error(`The worksheet type "${worksheetType}" is not supported for batch update.`);
  }
  if (!webApi[config.queryEndpoint]) {
    throw new Error(`The worksheet type "${worksheetType}" does not have a query endpoint '${config.queryEndpoint}'.`);
  }
  if (config.batchUpdateEndpoint && !webApi[config.batchUpdateEndpoint]) {
    throw new Error(
      `The worksheet type "${worksheetType}" batch update endpoint '${config.batchUpdateEndpoint}' could not be found.`
    );
  }

  return config;
};

/**
 * Returns an observable that will call the QEST Web API to get a set of worksheets and emits the returned worksheets
 *
 * @param {string} worksheetType The type of worksheet to get
 * @param {any} queryParams The query parameters to use (via the uri)
 */
export const callApiGetWorksheets = (worksheetType: string, queryParams: any) => {
  let { queryEndpoint: endpoint, responseTestsPropertyName } = retrieveEndpointConfig(worksheetType);

  return callApi(webApi[endpoint].get(queryParams)).pipe(
    map((response) => response.Body[responseTestsPropertyName].map((w: any) => camelizeObjectKeys(w)))
  );
};

/**
 * Returns an observable that will call the QEST Web API to update a batch of worksheets and emits the returned (updated) worksheets
 *
 * @param {string} worksheetType The type of worksheet to get
 * @param {Array<BatchUpdateSubmissionModel>} updates The set of updates to be made
 */
export const callApiBatchUpdateWorksheets = (worksheetType: string, updates: Array<BatchUpdateSubmissionModel>) => {
  let { batchUpdateEndpoint, responseTestsPropertyName } = retrieveEndpointConfig(worksheetType);

  if (hasEmptyValue(batchUpdateEndpoint)) {
    throw new Error(`The worksheet type "${worksheetType}" does not have a batch update endpoint defined.`);
  }

  return callApi(webApi[batchUpdateEndpoint!].post({ Body: { Updates: updates } as BatchUpdateRequestModel })).pipe(
    map((r) => r.Body[responseTestsPropertyName].map(camelizeObjectKeys))
  );
};

interface PersonReferenceSubmissionModel {
  Id: Guid;
}

interface WorksheetBatchTestedByCheckedBySubmissionModel {
  Ids: Array<Guid>;
  Date: string | null | undefined; //date
  Person: PersonReferenceSubmissionModel | null | undefined;
}

/**
 * Returns an observable that will call the QEST Web API to submit a batch checked by submission request
 */
export const callApiBatchCheckWorksheets = (requestModel: WorksheetBatchTestedByCheckedBySubmissionModel) => {
  return callApi(webApi['worksheets.batchUpdateCheckedBy'].post({ Body: requestModel }));
};
